import { LitElement, html, css } from 'lit';
import '@spectrum-web-components/theme/sp-theme.js';
import '@spectrum-web-components/theme/src/themes.js';
import '@spectrum-web-components/tabs/sp-tabs.js';
import '@spectrum-web-components/tabs/sp-tab.js';
import '@spectrum-web-components/tabs/sp-tab-panel.js';
import '@spectrum-web-components/textfield/sp-textfield.js';
import '@spectrum-web-components/button/sp-button.js';
import '@spectrum-web-components/progress-circle/sp-progress-circle.js';
import '@spectrum-web-components/alert-banner/sp-alert-banner.js';
import '@spectrum-web-components/picker/sp-picker.js';
import '@spectrum-web-components/menu/sp-menu-item.js';
import './markup-storage-tab.js';
import './products-tab.js';
import './change-detector-tab.js';
import './renderer-tab.js';
import './settings-tab.js';
import './change-detector-logs-tab.js';
import './product-catalog-tab.js';
import { TABS, ROUTES } from './routes.js';

// Simple client-side router
class Router {
    constructor(routes = {}) {
        this.routes = routes;
        this.currentRoute = '';

        // Handle browser back/forward and hash changes
        window.addEventListener('hashchange', () => {
            this.handleRoute(window.location.hash);
        });

        // Handle initial route
        this.handleRoute(window.location.hash);
    }

    navigate(path) {
        // Ensure path starts with #/ for hash routing
        const hashPath = path.startsWith('#/') ? path : `#/${path}`;
        window.location.hash = hashPath;
    }

    handleRoute(hash) {
        // Remove #/ and get only the path part.
        const route = (hash.replace(/^#\/?/, '').split('?')[0]) || '';
        this.currentRoute = route;

        console.log('Router handling route:', { hash, route, availableRoutes: Object.keys(this.routes) });

        // Find matching route or use default
        const handler = this.routes[route] || this.routes['default'];
        if (handler) {
            console.log('Route handler found, calling handler for route:', route);
            handler(route);
        } else {
            console.log('No route handler found for route:', route);
        }
    }
}

// Context management system
class ContextManager {
    constructor() {
        this.contexts = this.loadContexts();
        this.activeContextId = localStorage.getItem('activeContextId') || null;

        // If no active context but contexts exist, use the first one
        if (!this.activeContextId && this.contexts.length > 0) {
            this.activeContextId = this.contexts[0].id;
            this.saveActiveContextId();
        }
    }

    loadContexts() {
        const stored = localStorage.getItem('contexts');
        return stored ? JSON.parse(stored) : [];
    }

    saveContexts() {
        localStorage.setItem('contexts', JSON.stringify(this.contexts));
    }

    saveActiveContextId() {
        localStorage.setItem('activeContextId', this.activeContextId);
    }

    generateContextId(org, site) {
        return `${org}/${site}`;
    }

    createContext(org, site, appbuilderProjectJSON, aemAdminJWT) {
        const id = this.generateContextId(org, site);
        const existingIndex = this.contexts.findIndex(ctx => ctx.id === id);

        const context = {
            id,
            org,
            site,
            appbuilderProjectJSON,
            aemAdminJWT,
            annotations: []
        };

        if (existingIndex >= 0) {
            this.contexts[existingIndex] = context;
        } else {
            this.contexts.push(context);
        }

        this.saveContexts();
        return context;
    }

    getActiveContext() {
        return this.contexts.find(ctx => ctx.id === this.activeContextId) || null;
    }

    setActiveContext(contextId) {
        if (this.contexts.find(ctx => ctx.id === contextId)) {
            this.activeContextId = contextId;
            this.saveActiveContextId();
            return true;
        }
        return false;
    }

    deleteContext(contextId) {
        const index = this.contexts.findIndex(ctx => ctx.id === contextId);
        if (index >= 0) {
            this.contexts.splice(index, 1);

            // If deleted context was active, switch to first available or null
            if (this.activeContextId === contextId) {
                this.activeContextId = this.contexts.length > 0 ? this.contexts[0].id : null;
                this.saveActiveContextId();
            }

            this.saveContexts();
            return true;
        }
        return false;
    }

    getContextCredentials(contextId = null) {
        const context = contextId ?
            this.contexts.find(ctx => ctx.id === contextId) :
            this.getActiveContext();

        if (!context) return null;

        try {
            const projectData = context.appbuilderProjectJSON;
            const namespace = projectData?.project?.workspace?.details?.runtime?.namespaces?.[0];

            return {
                aemAdminToken: context.aemAdminJWT,
                aioAuth: namespace?.auth || '',
                aioNamespace: namespace?.name || '',
                projectName: projectData ?
                    `${projectData.project.name} - ${projectData.project.title} (${projectData.project.id})` :
                    '',
                org: context.org,
                site: context.site,
                siteToken: context.siteToken
            };
        } catch (error) {
            console.error('Error parsing context credentials:', error);
            return null;
        }
    }
}

export class AppMain extends LitElement {
    static properties = {
        selectedTab: { type: String },
        activeContextId: { state: true },
        contexts: { state: true },
        queryParams: { state: true },
    };

    static styles = css`
        :host {
            display: block;
            background-color: #1a1a1a;
            color: #ffffff;
            margin: 0;
            padding: 20px;
        }

        .app-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 20px;
            padding: 16px 20px;
            background-color: #242424;
            border-radius: 8px;
            border: 1px solid #333333;
        }

        .app-title {
            font-size: 24px;
            font-weight: bold;
            color: #ffffff;
        }

        .context-selector {
            display: flex;
            align-items: center;
            gap: 12px;
        }

        .context-label {
            font-size: 14px;
            color: #cccccc;
        }

        sp-theme {
            display: block;
            width: 100%;
            height: 100%;
        }

        .jwt-warning-container {
            position: sticky;
            top: 0;
            z-index: 1000;
            margin-bottom: 8px;
        }
        
        .jwt-warning-banner {
            margin-bottom: 4px;
        }
        
        .jwt-warning-banner:last-child {
            margin-bottom: 0;
        }
    `;

    constructor() {
        super();
        this.selectedTab = TABS.PRODUCTS;
        this.queryParams = {};


        // Initialize context manager
        this.contextManager = new ContextManager();
        this.activeContextId = this.contextManager.activeContextId;
        this.contexts = this.contextManager.contexts;

        // Route to tab mapping
        this.tabRoutes = {
            [ROUTES.PRODUCTS]: TABS.PRODUCTS,
            'detector': TABS.CHANGE_DETECTOR, // legacy alias
            [ROUTES.CHANGE_DETECTOR]: TABS.CHANGE_DETECTOR,
            [ROUTES.RENDERER]: TABS.RENDERER,
            [ROUTES.CHANGE_DETECTOR_LOGS]: TABS.CHANGE_DETECTOR_LOGS,
            'storage': TABS.MARKUP_STORAGE, // legacy alias
            [ROUTES.MARKUP_STORAGE]: TABS.MARKUP_STORAGE,
            [ROUTES.SETTINGS]: TABS.SETTINGS,
            [ROUTES.PRODUCT_CATALOG]: TABS.PRODUCT_CATALOG,
            'default': TABS.PRODUCTS
        };

        // Tab to route mapping (reverse)
        this.tabToRoute = {
            [TABS.PRODUCTS]: ROUTES.PRODUCTS,
            [TABS.CHANGE_DETECTOR]: ROUTES.CHANGE_DETECTOR,
            [TABS.RENDERER]: ROUTES.RENDERER,
            [TABS.CHANGE_DETECTOR_LOGS]: ROUTES.CHANGE_DETECTOR_LOGS,
            [TABS.MARKUP_STORAGE]: ROUTES.MARKUP_STORAGE,
            [TABS.SETTINGS]: ROUTES.SETTINGS,
            [TABS.PRODUCT_CATALOG]: ROUTES.PRODUCT_CATALOG
        };

        // Initialize router
        this.router = new Router({
            ...Object.fromEntries(
                Object.entries(this.tabRoutes).map(([route, tabId]) => [
                    route,
                    () => this.handleRouteWithContext(route, tabId)
                ])
            )
        });

        window.addEventListener('hashchange', () => this.updateQueryParams());
        this.updateQueryParams();
    }

    // JWT Validation Methods
    parseJWTExpiration(jwtToken) {
        if (!jwtToken || typeof jwtToken !== 'string') return null;

        try {
            const parts = jwtToken.split('.');
            if (parts.length !== 3) return null;

            const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));

            if (!payload.exp) return null;

            const expTimestamp = payload.exp * 1000;
            const now = Date.now();
            const daysRemaining = Math.ceil((expTimestamp - now) / (1000 * 60 * 60 * 24));

            return {
                expTimestamp,
                daysRemaining,
                isExpired: daysRemaining <= 0,
                isExpiringSoon: daysRemaining > 0 && daysRemaining <= 30
            };
        } catch (error) {
            return null;
        }
    }

    getCurrentJWTWarning() {
        const currentContext = this.contexts.find(c => c.id === this.activeContextId);
        if (!currentContext?.aemAdminJWT) return null;

        const jwtInfo = this.parseJWTExpiration(currentContext.aemAdminJWT);
        if (!jwtInfo) return null;

        if (jwtInfo.isExpired) {
            return {
                type: 'expired',
                message: `JWT token for ${currentContext.id} has expired`,
                contextId: currentContext.id
            };
        }
        if (jwtInfo.isExpiringSoon) {
            return {
                type: 'expiring',
                message: `JWT token for ${currentContext.id} expires in ${jwtInfo.daysRemaining} days`,
                contextId: currentContext.id,
                daysRemaining: jwtInfo.daysRemaining
            };
        }
        return null;
    }

    handleRouteWithContext(route, tabId) {
        // First, check for applicationName query parameter and set context if needed
        const hash = window.location.hash;
        const queryString = hash.split('?')[1] || '';
        const params = new URLSearchParams(queryString);
        const applicationName = params.get('applicationName');

        if (applicationName) {
            const potentialContextId = decodeURIComponent(applicationName);
            const contextExists = this.contextManager.contexts.some(ctx => ctx.id === potentialContextId);

            if (contextExists && this.activeContextId !== potentialContextId) {
                console.log('Setting context from applicationName:', potentialContextId);
                this._setActiveContext(potentialContextId);
            }
        }

        // Then proceed with tab selection
        this.setSelectedTab(tabId);
    }

    connectedCallback() {
        super.connectedCallback();
        this.checkLocalStorage();
        window.addEventListener('context-updated', this.handleContextUpdate.bind(this));
        window.addEventListener('set-active-context', this.handleSetActiveContext.bind(this));
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        window.removeEventListener('context-updated', this.handleContextUpdate.bind(this));
        window.removeEventListener('set-active-context', this.handleSetActiveContext.bind(this));
    }

    _setActiveContext(contextId) {
        if (this.contextManager.setActiveContext(contextId)) {
            this.activeContextId = contextId;
            this.dispatchEvent(new CustomEvent('context-changed', {
                detail: { contextId: contextId },
                bubbles: true,
                composed: true
            }));
        }
    }

    setSelectedTab(tabId) {
        if (this.tabRoutes[this.tabToRoute[tabId]]) {
            this.selectedTab = tabId;
        }
    }

    checkLocalStorage() {
        console.log('Loaded contexts:', this.contexts);
        console.log('Active context:', this.activeContextId);
    }

    handleContextChange(event) {
        const newContextId = event.target.value;
        this._setActiveContext(newContextId);

        // Update URL with applicationName parameter
        const currentUrl = window.location.href;
        const url = new URL(currentUrl);

        // Clear any existing applicationName from the main query string
        url.searchParams.delete('applicationName');

        // Handle the hash part (everything after #)
        const hash = url.hash;
        if (hash) {
            const [hashPath, hashQuery] = hash.split('?');
            const hashParams = new URLSearchParams(hashQuery || '');

            // Set the applicationName in the hash query parameters
            hashParams.set('applicationName', newContextId);

            // Reconstruct the hash
            url.hash = `${hashPath}?${hashParams.toString()}`;
        } else {
            // If no hash, just add it to the main URL
            url.searchParams.set('applicationName', newContextId);
        }

        // Update the URL without triggering a page reload
        window.history.replaceState(null, '', url.toString());

        // Update our internal query params
        this.updateQueryParams();
    }

    handleSetActiveContext(event) {
        const { contextId } = event.detail;
        if (contextId) {
            this._setActiveContext(contextId);
        }
    }

    handleContextUpdate(event) {
        const { org, site, appbuilderProjectJSON, aemAdminJWT } = event.detail;

        if (org && site) {
            const context = this.contextManager.createContext(org, site, appbuilderProjectJSON, aemAdminJWT);
            this.contexts = [...this.contextManager.contexts];

            // Set as active context
            this.contextManager.setActiveContext(context.id);
            this.activeContextId = context.id;


            // Trigger context change event
            this.dispatchEvent(new CustomEvent('context-changed', {
                detail: { contextId: context.id },
                bubbles: true,
                composed: true
            }));
        }
    }

    handleContextDelete(contextId) {
        if (this.contextManager.deleteContext(contextId)) {
            this.contexts = [...this.contextManager.contexts];
            this.activeContextId = this.contextManager.activeContextId;

            // Trigger context change event
            this.dispatchEvent(new CustomEvent('context-changed', {
                detail: { contextId: this.activeContextId },
                bubbles: true,
                composed: true
            }));
        }
    }

    getActiveContext() {
        return this.contextManager.getActiveContext();
    }

    getActiveCredentials() {
        return this.contextManager?.getContextCredentials();
    }

    // JWT Warning UI Methods
    renderJWTWarnings() {
        const warning = this.getCurrentJWTWarning();
        if (!warning) return '';

        return html`
            <div class="jwt-warning-container">
                ${warning.type === 'expired' ? html`
                    <div style="background-color: #fef2f2; border: 2px solid #ef4444; color: #dc2626; padding: 12px; margin-bottom: 8px; border-radius: 6px;">
                        <strong>🚨 JWT Token Expired</strong><br>
                        ${warning.message}
                        <br><br>
                        <div style="font-size: 14px; margin-bottom: 8px;">
                            Please update JWT token in the AEM Commerce Prerender App and in the Storefront Prerender UI configuration
                        </div>
                        <button @click="${this.navigateToSettings}" style="background: #ef4444; color: white; border: none; padding: 6px 12px; border-radius: 4px; cursor: pointer;">
                            Update context in Settings
                        </button>
                    </div>
                ` : html`
                    <div style="background-color: #fef7e0; border: 2px solid #f59e0b; color: #92400e; padding: 12px; margin-bottom: 8px; border-radius: 6px;">
                        <strong>⚠️ JWT Token Expiring Soon</strong><br>
                        ${warning.message}
                        <br><br>
                        <div style="font-size: 14px; margin-bottom: 8px;">
                            Please update JWT token in the AEM Commerce Prerender App and in the Storefront Prerender UI configuration
                        </div>
                        <button @click="${this.navigateToSettings}" style="background: #f59e0b; color: white; border: none; padding: 6px 12px; border-radius: 4px; cursor: pointer;">
                            Update context in Settings
                        </button>
                    </div>
                `}
            </div>
        `;
    }

    navigateToSettings() {
        this.setSelectedTab(TABS.SETTINGS);
        window.location.hash = `#/${ROUTES.SETTINGS}`;
    }

    handleTabChange(event) {
        const newTabId = event.target.selected;
        // Only navigate if the tab ID is valid and known.
        if (newTabId && this.tabToRoute[newTabId] && this.selectedTab !== newTabId) {
            const route = this.tabToRoute[newTabId];
            this.router.navigate(route);
        }
    }

    updateQueryParams() {
        const hash = window.location.hash;
        const queryString = hash.split('?')[1] || '';
        const params = new URLSearchParams(queryString);
        this.queryParams = Object.fromEntries(params.entries());
    }

    render() {
        if (!this.contexts.length) {
            return html`
                <sp-theme theme="spectrum" color="dark" scale="medium">
                    <settings-tab
                        .contextManager=${this.contextManager}
                        @context-updated=${this.handleContextUpdate}
                    ></settings-tab>
                </sp-theme>
            `;
        }

        return html`
            <sp-theme theme="spectrum" color="dark" scale="medium">
                ${this.renderJWTWarnings()}
                <div class="app-header">
                    <div class="app-title">Storefront Prerender</div>
                    <div class="context-selector">
                        <span class="context-label">Active Context:</span>
                        <sp-picker
                            label="Select a context"
                            @change=${this.handleContextChange}
                            .value=${this.activeContextId}
                        >
                            ${this.contexts.map(context => html`
                                <sp-menu-item value=${context.id}>
                                    ${context.id}
                                </sp-menu-item>
                            `)}
                        </sp-picker>
                    </div>
                </div>

                <sp-tabs selected=${this.selectedTab} @change=${this.handleTabChange}>
                    <sp-tab label="Published Products" value=${TABS.PRODUCTS}></sp-tab>
                    <sp-tab label="Product Change Detector" value=${TABS.CHANGE_DETECTOR}></sp-tab>
                    <sp-tab label="Renderer" value=${TABS.RENDERER}></sp-tab>
                    <sp-tab label="Logs & Activations" value=${TABS.CHANGE_DETECTOR_LOGS}></sp-tab>
                    <sp-tab label="Markup Storage" value=${TABS.MARKUP_STORAGE}></sp-tab>
                    <sp-tab label="Settings" value=${TABS.SETTINGS}></sp-tab>
                    <sp-tab label="Product Catalog" value=${TABS.PRODUCT_CATALOG}></sp-tab>

                    <sp-tab-panel value=${TABS.PRODUCTS}>
                        <products-tab .contextManager=${this.contextManager} .activeContextId=${this.activeContextId}></products-tab>
                    </sp-tab-panel>
                    <sp-tab-panel value=${TABS.CHANGE_DETECTOR}>
                        <change-detector-tab .contextManager=${this.contextManager} .activeContextId=${this.activeContextId}></change-detector-tab>
                    </sp-tab-panel>
                    <sp-tab-panel value=${TABS.RENDERER}>
                        <renderer-tab
                            .contextManager=${this.contextManager}
                            .activeContextId=${this.activeContextId}
                            .queryParams=${this.queryParams}
                        ></renderer-tab>
                    </sp-tab-panel>
                    <sp-tab-panel value=${TABS.CHANGE_DETECTOR_LOGS}>
                        <change-detector-logs-tab
                            .contextManager=${this.contextManager}
                            .queryParams=${this.queryParams}
                            .activeContextId=${this.activeContextId}
                            @set-active-context=${this.handleSetActiveContext}
                        ></change-detector-logs-tab>
                    </sp-tab-panel>
                    <sp-tab-panel value=${TABS.MARKUP_STORAGE}>
                        <markup-storage-tab .contextManager=${this.contextManager} .activeContextId=${this.activeContextId}></markup-storage-tab>
                    </sp-tab-panel>
                    <sp-tab-panel value=${TABS.SETTINGS}>
                        <settings-tab
                            .contexts=${this.contexts}
                            .activeContextId=${this.activeContextId}
                            @context-change=${this.handleContextChange}
                            @context-update=${this.handleContextUpdate}
                            @context-delete=${this.handleContextDelete}
                        ></settings-tab>
                    </sp-tab-panel>
                    <sp-tab-panel value=${TABS.PRODUCT_CATALOG}>
                        <product-catalog-tab
                            .contextManager=${this.contextManager}
                            .activeContextId=${this.activeContextId}
                            .queryParams=${this.queryParams}
                        ></product-catalog-tab>
                    </sp-tab-panel>
                </sp-tabs>
            </sp-theme>
        `;
    }
}

customElements.define('app-main', AppMain); 